import React, { useState, useEffect, useCallback } from 'react';
import api from './axiosConfig';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  Chip,
  Divider,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  Tooltip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Info,
  Warning,
  CheckCircle,
  Error,
  Schedule,
  LocalHospital,
  ChildCare,
  Help,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

// ============================================================
// CONSTANTS
// ============================================================

const MAX_WEEKS = 12;
const DEADLINE_WEEKS = 68;
const MIN_NEONATAL_DAYS = 7;
const PAY_SERVICE_WEEKS = 26;
const EFFECTIVE_DATE = new Date('2025-04-06');

// ============================================================
// HELPER FUNCTIONS
// ============================================================

const calculateWeeksRemaining = (entitlement) => {
  if (!entitlement) return MAX_WEEKS;
  return Math.max(0, MAX_WEEKS - (entitlement.weeks_used_tier1 || 0) - (entitlement.weeks_used_tier2 || 0));
};

const calculateDeadlineDate = (babyDob) => {
  if (!babyDob) return null;
  const dob = new Date(babyDob);
  const deadline = new Date(dob);
  deadline.setDate(deadline.getDate() + (DEADLINE_WEEKS * 7));
  return deadline;
};

const calculateDaysUntilDeadline = (babyDob) => {
  const deadline = calculateDeadlineDate(babyDob);
  if (!deadline) return null;
  const today = new Date();
  const diffTime = deadline - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const calculateNeonatalDays = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = end - start;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both days
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const NeonatalCareFields = ({ 
  formData, 
  onChange, 
  employeeId, 
  existingEntitlement,
  employeeServiceWeeks 
}) => {
  const [entitlement, setEntitlement] = useState(existingEntitlement || null);
  const [loading, setLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [error, setError] = useState(null);

  // Determine current tier based on form data
  const isDischargedNow = formData.baby_discharged === 'yes';
  const currentTier = isDischargedNow ? 'tier2' : 'tier1';
  
  // Calculate values
  const weeksRemaining = calculateWeeksRemaining(entitlement);
  const daysUntilDeadline = formData.baby_dob ? calculateDaysUntilDeadline(formData.baby_dob) : null;
  const deadlineDate = formData.baby_dob ? calculateDeadlineDate(formData.baby_dob) : null;
  const neonatalDays = calculateNeonatalDays(formData.neonatal_start_date, formData.discharge_date);
  const meetsMinDays = neonatalDays === null || neonatalDays >= MIN_NEONATAL_DAYS;
  
  // Check if eligible for pay
  const eligibleForPay = employeeServiceWeeks >= PAY_SERVICE_WEEKS;

  // Check if legislation is in effect
  const legislationInEffect = new Date() >= EFFECTIVE_DATE;

  // Load existing entitlement when employee/baby changes
  useEffect(() => {
    if (employeeId && formData.baby_dob && !existingEntitlement) {
      loadEntitlement();
    }
  }, [employeeId, formData.baby_dob]);

  const loadEntitlement = async () => {
    try {
      setLoading(true);
      const response = await api.get('/time-off/neonatal/entitlement', {
        params: {
          employee_id: employeeId,
          baby_dob: formData.baby_dob
        }
      });
      if (response.data) {
        setEntitlement(response.data);
        // Pre-fill form if we have existing data
        if (response.data.discharge_date && !formData.discharge_date) {
          onChange('discharge_date', response.data.discharge_date);
          onChange('baby_discharged', 'yes');
        }
      }
    } catch (err) {
      // No existing entitlement - that's fine, this is a new request
      setEntitlement(null);
    } finally {
      setLoading(false);
    }
  };

  // Update tier in form data when it changes
  useEffect(() => {
    onChange('neonatal_tier', currentTier);
  }, [currentTier]);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <Box>
      {/* Legislation Status Banner */}
      {!legislationInEffect && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Coming April 2025</AlertTitle>
          Neonatal Care Leave becomes law on 6 April 2025. You can prepare requests now, 
          but they cannot be processed until the legislation is in effect.
        </Alert>
      )}

      {/* Help Toggle */}
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Chip
          icon={showHelp ? <ExpandLess /> : <Help />}
          label={showHelp ? 'Hide guidance' : 'Show guidance'}
          onClick={() => setShowHelp(!showHelp)}
          variant="outlined"
          size="small"
        />
      </Box>

      {/* Expandable Guidance */}
      <Collapse in={showHelp}>
        <Card variant="outlined" sx={{ mb: 3, backgroundColor: 'info.lighter' }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              📋 Neonatal Care Leave - Key Facts
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Day-one right" 
                  secondary="No minimum service required for LEAVE (pay requires 26 weeks)"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Schedule fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Up to 12 weeks" 
                  secondary="Must be used within 68 weeks of baby's birth"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><LocalHospital fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Minimum 7 days neonatal care" 
                  secondary="Baby must have been in neonatal care for at least 7 continuous days"
                />
              </ListItem>
            </List>
            
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="primary" fontWeight="bold">
                  TIER 1 - While baby in care
                </Typography>
                <Typography variant="body2">
                  Take leave in separate weeks (minimum 1 week blocks). 
                  Flexible timing while your baby is in neonatal care.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="secondary" fontWeight="bold">
                  TIER 2 - After discharge
                </Typography>
                <Typography variant="body2">
                  All remaining leave must be taken in one consecutive block. 
                  Once you start Tier 2, you cannot return to Tier 1.
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {/* Entitlement Status (if they have previous requests) */}
      {entitlement && (
        <Card variant="outlined" sx={{ mb: 3, borderColor: 'primary.main' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Your Neonatal Care Leave Entitlement
              </Typography>
              <Chip 
                label={`${weeksRemaining} weeks remaining`}
                color={weeksRemaining > 4 ? 'success' : weeksRemaining > 0 ? 'warning' : 'error'}
              />
            </Box>
            
            {/* Progress Bar */}
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="caption">Used: {MAX_WEEKS - weeksRemaining} weeks</Typography>
                <Typography variant="caption">Total: {MAX_WEEKS} weeks</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={((MAX_WEEKS - weeksRemaining) / MAX_WEEKS) * 100}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Tier 1 Used</Typography>
                <Typography variant="body1">{entitlement.weeks_used_tier1 || 0} weeks</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Tier 2 Used</Typography>
                <Typography variant="body1">{entitlement.weeks_used_tier2 || 0} weeks</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Current Tier</Typography>
                <Chip 
                  label={entitlement.current_tier === 'tier1' ? 'Tier 1' : 'Tier 2'}
                  size="small"
                  color={entitlement.current_tier === 'tier1' ? 'primary' : 'secondary'}
                />
              </Grid>
            </Grid>

            {/* Tier 2 Lock Warning */}
            {entitlement.tier2_locked && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>Tier 2 In Progress</AlertTitle>
                You have started your Tier 2 leave block. All remaining leave must be taken 
                consecutively from {formatDate(entitlement.tier2_start_date)}.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Baby Details Section */}
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Baby & Hospital Details
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="Baby's Date of Birth"
            value={formData.baby_dob || ''}
            onChange={(e) => onChange('baby_dob', e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            helperText="Used to calculate 68-week deadline"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="Neonatal Care Start Date"
            value={formData.neonatal_start_date || ''}
            onChange={(e) => onChange('neonatal_start_date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            helperText="Date baby was admitted to neonatal unit"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Hospital Name"
            value={formData.hospital_name || ''}
            onChange={(e) => onChange('hospital_name', e.target.value)}
            placeholder="e.g., Royal Manchester Children's Hospital"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Baby's Name (Optional)"
            value={formData.baby_name || ''}
            onChange={(e) => onChange('baby_name', e.target.value)}
            helperText="Helps identify if you have multiple children"
          />
        </Grid>
      </Grid>

      {/* Deadline Warning */}
      {daysUntilDeadline !== null && (
        <Box mt={2}>
          {daysUntilDeadline <= 0 ? (
            <Alert severity="error">
              <AlertTitle>Deadline Passed</AlertTitle>
              The 68-week deadline for this baby has passed ({formatDate(deadlineDate)}). 
              Neonatal care leave is no longer available.
            </Alert>
          ) : daysUntilDeadline <= 28 ? (
            <Alert severity="warning">
              <AlertTitle>Deadline Approaching</AlertTitle>
              You have <strong>{daysUntilDeadline} days</strong> remaining to use your 
              neonatal care leave (deadline: {formatDate(deadlineDate)}).
            </Alert>
          ) : (
            <Alert severity="info">
              Leave must be used by <strong>{formatDate(deadlineDate)}</strong> ({daysUntilDeadline} days remaining)
            </Alert>
          )}
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Tier Selection Section */}
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Baby's Current Status
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              value={formData.baby_discharged || ''}
              onChange={(e) => {
                onChange('baby_discharged', e.target.value);
                // Clear discharge date if switching back to 'no'
                if (e.target.value === 'no') {
                  onChange('discharge_date', '');
                }
              }}
            >
              <FormControlLabel 
                value="no" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography variant="body1">
                      Baby is still in neonatal care
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tier 1 - You can take leave in non-consecutive weeks
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel 
                value="yes" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography variant="body1">
                      Baby has been discharged from neonatal care
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tier 2 - Remaining leave must be taken consecutively
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Discharge Date (if discharged) */}
        {isDischargedNow && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Discharge Date"
              value={formData.discharge_date || ''}
              onChange={(e) => onChange('discharge_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              helperText="Date baby was discharged from neonatal care"
            />
          </Grid>
        )}

        {/* Minimum Days Check */}
        {isDischargedNow && neonatalDays !== null && (
          <Grid item xs={12}>
            {meetsMinDays ? (
              <Alert severity="success">
                <CheckCircle sx={{ mr: 1 }} />
                Baby was in neonatal care for <strong>{neonatalDays} days</strong> - 
                meets minimum requirement of 7 days.
              </Alert>
            ) : (
              <Alert severity="error">
                <AlertTitle>Does Not Qualify</AlertTitle>
                Baby was in neonatal care for <strong>{neonatalDays} days</strong>. 
                Neonatal Care Leave requires a minimum of 7 continuous days.
              </Alert>
            )}
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Leave Request Section */}
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Leave Request - {currentTier === 'tier1' ? 'Tier 1' : 'Tier 2'}
      </Typography>

      {/* Tier-specific guidance */}
      <Alert 
        severity={currentTier === 'tier1' ? 'info' : 'warning'} 
        sx={{ mb: 3 }}
      >
        {currentTier === 'tier1' ? (
          <>
            <AlertTitle>Tier 1 - Flexible Leave</AlertTitle>
            You can take leave in separate weeks while your baby is in neonatal care. 
            Each request must be for at least 1 full week.
          </>
        ) : (
          <>
            <AlertTitle>Tier 2 - Consecutive Leave</AlertTitle>
            After discharge, any remaining leave must be taken in <strong>one consecutive block</strong>. 
            Once you start Tier 2 leave, you cannot take any more Tier 1 leave.
          </>
        )}
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="Leave Start Date"
            value={formData.start_date || ''}
            onChange={(e) => onChange('start_date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Weeks Requested</InputLabel>
            <Select
              value={formData.neonatal_weeks || ''}
              onChange={(e) => onChange('neonatal_weeks', e.target.value)}
              label="Weeks Requested"
              required
            >
              {[...Array(Math.min(weeksRemaining, MAX_WEEKS))].map((_, i) => (
                <MenuItem key={i + 1} value={i + 1}>
                  {i + 1} week{i > 0 ? 's' : ''}
                  {currentTier === 'tier2' && i + 1 === weeksRemaining && ' (all remaining)'}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {weeksRemaining} weeks remaining of {MAX_WEEKS} week entitlement
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Tier 2 Warning - Taking All Leave */}
        {currentTier === 'tier2' && formData.neonatal_weeks && formData.neonatal_weeks < weeksRemaining && (
          <Grid item xs={12}>
            <Alert severity="warning">
              <AlertTitle>Important: Tier 2 Consecutive Requirement</AlertTitle>
              You are requesting {formData.neonatal_weeks} of your {weeksRemaining} remaining weeks. 
              In Tier 2, you should request ALL remaining weeks as they must be taken consecutively. 
              Any weeks not requested now may be forfeited.
            </Alert>
          </Grid>
        )}

        {/* End Date Display (calculated) */}
        {formData.start_date && formData.neonatal_weeks && (
          <Grid item xs={12} sm={6}>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Leave End Date:</strong>{' '}
                {formatDate(
                  new Date(
                    new Date(formData.start_date).getTime() + 
                    (formData.neonatal_weeks * 7 - 1) * 24 * 60 * 60 * 1000
                  )
                )}
              </Typography>
            </Alert>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Pay Eligibility Section */}
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Statutory Neonatal Care Pay
      </Typography>

      <Card variant="outlined" sx={{ backgroundColor: eligibleForPay ? 'success.lighter' : 'warning.lighter' }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            {eligibleForPay ? (
              <CheckCircle color="success" />
            ) : (
              <Warning color="warning" />
            )}
            <Typography variant="subtitle2" fontWeight="bold">
              {eligibleForPay ? 'You may be eligible for Statutory Pay' : 'Pay eligibility not met'}
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            {eligibleForPay ? (
              <>
                You have {employeeServiceWeeks} weeks service (26 weeks required). 
                Subject to earnings threshold and employer confirmation.
              </>
            ) : (
              <>
                Statutory Neonatal Care Pay requires 26 weeks continuous service. 
                You have {employeeServiceWeeks || 0} weeks. 
                <strong> You are still entitled to the LEAVE</strong> (day-one right), 
                but it will be unpaid unless your employer offers enhanced pay.
              </>
            )}
          </Typography>
        </CardContent>
      </Card>

      {/* Document Upload Prompt */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <AlertTitle>Supporting Documents</AlertTitle>
        You may need to provide hospital confirmation of your baby's neonatal care. 
        This can be uploaded after submitting your request.
      </Alert>
    </Box>
  );
};

export default NeonatalCareFields;